// ExportToolbar.jsx
import React, { useState } from "react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { Download, Loader2 } from "lucide-react";

/**
 * Usage:
 * - Wrap each section you don't want split across pages with:  <div data-export-chunk>...</div>
 * - Place this toolbar above your report and pass a ref to the report container.
 *
 * Example:
 *   <ExportToolbar targetRef={reportRef} filename="raport-firmy" />
 */

// ---- Image & PDF tuning (balance quality vs size) ----
const TUNING = {
  pixelRatio: 2.2,       // 2.0–2.6: higher = sharper but larger PNG before JPEG re-encode
  jpegQuality: 0.84,     // 0.80–0.88 usually yields ~3–6 MB PDFs for multi-page reports
  compression: "MEDIUM", // "FAST" | "MEDIUM" | "SLOW" (jsPDF image compression)
};

// ---- Page layout (A4 portrait) ----
const PAGE_MARGIN_LR = 10; // mm (left/right)
const HEADER_H = 22;       // mm (taller to fit colored band + logo)
const FOOTER_H = 12;       // mm
const GAP_MM = 3;          // mm between stacked sections

// ---- Default header content (customize via props) ----
const DEFAULT_HEADER = {
  title: "Raport kontrahentów",
  subtitle: "AnalizyIRaporty.pl",
  logoDataUrl: null, // e.g. "data:image/png;base64,...."
  logoWmm: 12,
};

// ---- Theme (override via props) ----
const DEFAULT_THEME = {
  primary: "#d32f2f",   // 🔴 red
  primaryTextOnBand: "#ffffff",
  bodyBg: "#ffffff",
  divider: "#E6E6E6",
  footerText: "#6F6F6F",
  headerSubtitleText: "#FFECEF",
};

// Helpers
const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};

export default function ExportToolbar({
  targetRef,
  filename = "raport",
  headerTitle = DEFAULT_HEADER.title,
  headerSubtitle = DEFAULT_HEADER.subtitle,
  headerLogoDataUrl = DEFAULT_HEADER.logoDataUrl,
  theme = DEFAULT_THEME,
  buttonLabel = "Pobierz PDF",
  loadingLabel = "Pobieranie…",
  containerStyle,       // <-- NEW
}) {
  const [isExporting, setIsExporting] = useState(false);

  // Exclude toolbar itself from capture
  const filter = (node) => !node?.dataset?.noexport;

  // Shared options for html-to-image (skipFonts fixes cross-origin CSS/Google Fonts errors)
  const TO_IMG_OPTS = {
    pixelRatio: TUNING.pixelRatio,
    cacheBust: true,
    backgroundColor: theme.bodyBg,
    filter,
    skipFonts: true, // 💡 prevents SecurityError from cross-origin stylesheets
  };

  // Render a DOM node to an <img /> element (PNG data URL)
  const renderNodeToImage = async (node) =>
    new Promise(async (resolve, reject) => {
      try {
        if (document?.fonts?.ready) {
          try {
            await document.fonts.ready;
          } catch {}
        }
        const dataUrl = await toPng(node, TO_IMG_OPTS);
        const img = new Image();
        img.src = dataUrl;
        img.onload = () => resolve(img);
        img.onerror = reject;
      } catch (e) {
        console.error("Render error:", e);
        reject(e);
      }
    });

  // Draw header on current page (colored band + logo + title/subtitle)
  const drawHeader = (pdf, pageW) => {
    const bandY = PAGE_MARGIN_LR / 2;
    const bandX = PAGE_MARGIN_LR;
    const bandW = pageW - 2 * PAGE_MARGIN_LR;
    const bandH = HEADER_H - 6;

    // Colored band
    const { r: pr, g: pg, b: pb } = hexToRgb(theme.primary);
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(bandX, bandY, bandW, bandH, "F");

    // Inner content padding
    const padX = 8;
    const contentX = bandX + padX;
    const contentY = bandY + 6;

    let cursorX = contentX;

    // Optional logo
    if (headerLogoDataUrl) {
      try {
        const imgProps = pdf.getImageProperties(headerLogoDataUrl);
        const ar = imgProps.width / imgProps.height;
        const w = DEFAULT_HEADER.logoWmm;
        const h = w / ar;
        pdf.addImage(headerLogoDataUrl, "PNG", contentX, contentY - 4, w, h);
        cursorX += w + 6;
      } catch {
        /* ignore logo errors */
      }
    }

    // Title (white on band)
    const { r: tr, g: tg, b: tb } = hexToRgb(theme.primaryTextOnBand);
    pdf.setTextColor(tr, tg, tb);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text(headerTitle, cursorX, contentY + 1);

    // Subtitle (lighter)
    if (headerSubtitle) {
      const { r: sr, g: sg, b: sb } = hexToRgb(theme.headerSubtitleText);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(sr, sg, sb);
      pdf.text(headerSubtitle, cursorX, contentY + 7);
    }

    // Divider line under header
    const { r: dr, g: dg, b: db } = hexToRgb(theme.divider);
    pdf.setDrawColor(dr, dg, db);
    pdf.setLineWidth(0.2);
    pdf.line(PAGE_MARGIN_LR, bandY + bandH + 2, pageW - PAGE_MARGIN_LR, bandY + bandH + 2);

    // Reset text color to black for content
    pdf.setTextColor(0, 0, 0);
  };

  // Draw footer on every page after content is placed
  const drawFooterAllPages = (pdf) => {
    const total = pdf.getNumberOfPages();
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    const stamp = new Date().toLocaleString("pl-PL");
    const { r: dr, g: dg, b: db } = hexToRgb(theme.divider);
    const { r: fr, g: fg, b: fb } = hexToRgb(theme.footerText);

    for (let i = 1; i <= total; i++) {
      pdf.setPage(i);

      // Divider above footer
      pdf.setDrawColor(dr, dg, db);
      pdf.setLineWidth(0.2);
      pdf.line(PAGE_MARGIN_LR, pageH - FOOTER_H + 4, pageW - PAGE_MARGIN_LR, pageH - FOOTER_H + 4);

      // Left: timestamp
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(fr, fg, fb);
      pdf.setFontSize(9);
      pdf.text(stamp, PAGE_MARGIN_LR + 2, pageH - 4);

      // Right: pagination
      const pageLabel = `${i} / ${total}`;
      const textW = pdf.getTextWidth(pageLabel);
      pdf.text(pageLabel, pageW - PAGE_MARGIN_LR - 2 - textW, pageH - 4);

      // Reset text color
      pdf.setTextColor(0, 0, 0);
    }
  };

  // Main export (one-column packing, no splitting across sections)
  const exportPDF = async () => {
    if (!targetRef?.current || isExporting) return;
    setIsExporting(true);
    try {
      const container = targetRef.current;

      // Gather chunks: <div data-export-chunk>...</div>
      let chunks = Array.from(container.querySelectorAll("[data-export-chunk]"));
      if (chunks.length === 0) {
        // fallback: use direct children (except toolbar)
        chunks = Array.from(container.children).filter((el) => !el.dataset?.noexport);
      }

      // Render each chunk to an image
      const images = [];
      for (const el of chunks) {
        el.style.scrollMargin = "0"; // avoid weird margins while rendering
        const img = await renderNodeToImage(el);
        images.push(img);
      }

      // Create PDF & layout
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const drawW = pageW - 2 * PAGE_MARGIN_LR;
      const topY = PAGE_MARGIN_LR + HEADER_H;
      const bottomLimit = pageH - PAGE_MARGIN_LR - FOOTER_H;

      let y = topY;
      let firstPage = true;

      // Header on first page
      drawHeader(pdf, pageW);

      for (const img of images) {
        // Re-encode to JPEG to control final size (smaller than PNG)
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const jpeg = canvas.toDataURL("image/jpeg", TUNING.jpegQuality);

        const aspect = img.height / img.width;
        const drawH = drawW * aspect;

        // New page if not enough vertical space
        if (!firstPage && y + drawH > bottomLimit) {
          pdf.addPage();
          drawHeader(pdf, pageW);
          y = topY;
        }

        pdf.addImage(jpeg, "JPEG", PAGE_MARGIN_LR, y, drawW, drawH, undefined, TUNING.compression);

        y += drawH + GAP_MM;
        firstPage = false;
      }

      // Footer across all pages
      drawFooterAllPages(pdf);

      // Save
      pdf.save(`${filename}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
      // Optional: toast or alert here if you use a notification system
      alert("Nie udało się wygenerować PDF. Sprawdź konsolę przeglądarki.");
    } finally {
      setIsExporting(false);
    }
  };

  const btnBg = theme.primary;
  const btnBorder = theme.primary;

  return (
    <div
      data-noexport
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "flex-end",
        alignItems: "center",
        marginBottom: 8,
        ...containerStyle, // <-- allow caller to override alignment/margins
      }}
    >
      <button
        onClick={exportPDF}
        title={buttonLabel}
        disabled={isExporting}
        aria-busy={isExporting}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          border: `1px solid ${theme.primary}`,
          background: theme.primary,
          color: "#fff",
          padding: "8px 12px",
          borderRadius: 10,
          cursor: isExporting ? "not-allowed" : "pointer",
          fontWeight: 600,
          opacity: isExporting ? 0.85 : 1,
          transition: "transform 120ms ease, opacity 120ms ease",
          lineHeight: 1,               // helps icon/text vertical centering
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {isExporting ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
        {isExporting ? loadingLabel : buttonLabel}
      </button>

      <style>{`
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );

}
